import { Business } from "@turizi/shared";
import { useTranslation } from "react-i18next";
import { Badge } from "@mantine/core";
import { IconStar, IconExternalLink } from "@tabler/icons-react";
import { BUSINESSES_PATH } from "@/lib/navigation";
import { useNavigate, Link } from "react-router-dom";
import { getAttrValue } from "@/lib/models-utils";
import { localTenant } from "@/services/session-service";
import { TagIcon } from "@/lib/tag-utils";
import CustomCheckbox from "@/components/custom-checkbox";
import { BUSINESS_TEMPLATES } from "@/types/templates";

interface Props {
    businesses: Business[];
    selectedIds?: string[];
    toggleBusinessSelection?: (id: string) => void;
    enableBulkActions?: boolean;
}

export default function BusinessesTable({
    businesses,
    selectedIds = [],
    toggleBusinessSelection,
    enableBulkActions = false,
}: Props) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const tenant = localTenant();

    const handleRowClick = (business: Business) => {
        navigate(`${BUSINESSES_PATH}/${business.id}`);
    };

    return (
        <div className="subtle-table-sm">
            <table>
                <thead>
                    <tr>
                        {enableBulkActions && (
                            <th>
                                {/* Checkbox column header */}
                            </th>
                        )}
                        <th>#</th>
                        <th>{t('name')}</th>
                        <th>{t('category')}</th>
                        <th>{t('city')} / {t('country')}</th>
                        <th>{t('tax_id')}</th>
                        <th>{t('discount')}</th>
                        <th>{t('currency')}</th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                    {businesses.map((business) => {
                        const tenantBusiness = business.tenant_business;
                        const isSelected = selectedIds.includes(String(business.id));

                        // Hotel attributes
                        const isHotel = business.category === BUSINESS_TEMPLATES.hotel.category;
                        const stars = isHotel ? Number(getAttrValue(business, 'stars')) || 0 : 0;
                        const regime = isHotel ? getAttrValue(business, 'regime') : null;

                        return (
                            <tr
                                key={business.id}
                                className={`hover:bg-gray-50/50 cursor-pointer align-middle ${isSelected ? 'row-selected' : ''}`}
                                onClick={() => handleRowClick(business)}
                            >
                                {enableBulkActions && (
                                    <td onClick={(e) => e.stopPropagation()}>
                                        <CustomCheckbox
                                            checked={isSelected}
                                            onChange={() => toggleBusinessSelection?.(String(business.id))}
                                        />
                                    </td>
                                )}
                                <td>
                                    <div className="text-xs text-gray-400">#{business.id}</div>
                                </td>
                                <td>
                                    <div className="flex items-center gap-2">
                                        <TagIcon
                                            name={business?.category}
                                            size="xs"
                                            hideText
                                        />
                                        <div className="font-semibold text-gray-800">
                                            {business.name}
                                        </div>
                                        {isHotel && stars > 0 && (
                                            <div className="flex items-center gap-0.5">
                                                {Array.from({ length: stars }).map((_, i) => (
                                                    <IconStar key={i} size={12} color="#f59e0b" fill="#f59e0b" stroke={1} />
                                                ))}
                                            </div>
                                        )}
                                        {isHotel && regime && (
                                            <Badge color="green" variant="light" size="xs">
                                                {regime}
                                            </Badge>
                                        )}
                                        {tenantBusiness?.is_favorite && (
                                            <Badge color="yellow" variant="light" size="xs">
                                                {t('favorite')}
                                            </Badge>
                                        )}
                                    </div>
                                </td>
                                <td>
                                    <div className="capitalize text-xs text-gray-500">
                                        {t(business.category)}
                                    </div>
                                </td>
                                <td>
                                    <div className="text-xs text-gray-600">
                                        {business.city || '-'} {business.country_code ? `, ${business.country_code}` : ''}
                                    </div>
                                </td>
                                <td>
                                    {(business?.tax_id_type || business?.tax_id_number) ? (
                                        <span className="text-xs text-gray-600">
                                            {business.tax_id_type || 'Tax ID'}: {business.tax_id_number || '--'}
                                        </span>
                                    ) : (
                                        <span className="text-gray-400">-</span>
                                    )}
                                </td>
                                <td>
                                    {tenantBusiness?.discount_category ? (
                                        <Badge color="blue" variant="light" size="xs">
                                            {tenantBusiness.discount_category}
                                        </Badge>
                                    ) : (
                                        <span className="text-gray-400 text-xs">{t('no_discount')}</span>
                                    )}
                                </td>
                                <td>
                                    {tenantBusiness?.default_currency ? (
                                        <Badge color="orange" variant="light" size="xs">
                                            {tenantBusiness.default_currency}
                                        </Badge>
                                    ) : (
                                        <span className="text-gray-400">-</span>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
